

import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {IShortServiceProvider} from "@/model/shortInterfaces";
import {UrlHelper} from "@/helpers/UrlHelper";
import Datim from "@/components/shared/Datim.vue";
import {BoListPanel, IAnyObject} from "bonomo/dist";
import CreateButton from "@/components/shared/CreateButton.vue";
import ServiceProviderChip from "@/components/shared/chips/ServiceProviderChip.vue";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
import SPDuplicateDialog from "@/components/serviceProvider/SPDuplicateDialog.vue";
import ServiceProviderTypeChip from "@/components/shared/chips/ServiceProviderTypeChip.vue";
import SimpleConfirmationDialog from "@/components/shared/SimpleConfirmationDialog.vue";
import ServiceProviderActiveWidget from "@/components/serviceProvider/widget/ServiceProviderActiveWidget.vue";
import CsvDownloader from "@/components/shared/CsvDownloader.vue";
import PaginationBar from "@/components/shared/PaginationBar.vue";
import {serviceProviderService} from "@/services/serviceProviderService";
import {EServiceProviderType} from "@/model/Service/serviceProviderInterfaces";

const serviceProviderModule = namespace("serviceProviderModule");

@Component({
  components: {
    CsvDownloader,
    ServiceProviderActiveWidget,
    SimpleConfirmationDialog,
    SPDuplicateDialog,
    BoListPanel,
    ServiceProviderChip,
    ServiceProviderTypeChip,
    CreateButton,
    PaginationBar,
    Datim,
  }
})
export default class ServiceProviderList extends Vue {

  @serviceProviderModule.Getter('serviceProviderList')
  protected serviceProviderList!: IShortServiceProvider[];
  @serviceProviderModule.Getter('serviceProviderListCurrentPage')
  protected serviceProviderListCurrentPage!: number;
  @serviceProviderModule.Getter('serviceProviderListLastPage')
  protected serviceProviderListLastPage!: number;
  @serviceProviderModule.Getter('serviceProviderListTotal')
  protected serviceProviderListTotal!: number;
  @serviceProviderModule.Getter('serviceProviderListPerPage')
  protected serviceProviderListPerPage!: number;
  @serviceProviderModule.Action('fetchServiceProvidersList')
  protected fetchServiceProvidersList!: (filters: any) => Promise<any>;
  @serviceProviderModule.Action('setDemo')
  protected setDemo!: (payload: {serviceProviderId: number, isDemo: boolean}) => Promise<any>;

  protected spId: number = 0;

  protected get headers(): BetterDataTableHeader[] {
    // @ts-ignore
    return [
        {text: this.$t('common.id'), align: 'start', value: 'id'},
        {text: this.$t('common.name'), align: 'start', value: 'name'},
        {text: this.$t('common.type'), align: 'start', value: 'type'},
        {text: this.$t('services.provider.fields.address_town_city'), align: 'start', value: 'address_town_city'},
        {text: this.$t('common.active'), align: 'center', value: 'active'},
        {text: 'Demo', align: 'center', value: 'is_demo'},
        {text: 'Scope (Users)', align: 'end', value: 'scopeSize'},
        {text: 'Visit', align: 'end', value: 'visit'},
        {text: 'Purchase Intent', align: 'end', value: 'purchaseIntent'},
        {text: 'Orders', align: 'end', value: 'orderCount'},
        {text: '', value: 'detailButton', align: "end", class: "pr-0", cellClass: "pr-0"},
      ]
      .filter(each => (
          (each.value != 'address_town_city' || this.$vuetify.breakpoint.mdAndUp)
      ));
  }

  protected search: string = '';
  protected filterActive: string = '';
  protected filterType: string = '';
  protected filterIsDemo: string = '';
  protected filterDeleted: string = '';
  protected filtersExpanded: boolean = false;
  protected selectedPerPage: number = 20;
  protected searchTimer: any = null;
  protected sortBy: string = 'id';
  protected sortDesc: boolean = false;
  protected sortTimer: any = null;

  readonly perPageOptions: number[] = [20, 50, 100];

  protected showContextMenu = false;
  protected contextMenuTarget: IAnyObject = {};
  protected contextMenuX = 0;
  protected contextMenuY = 0;

  protected showDuplicateDialog: boolean = false;

  protected get hasActiveFilters(): boolean {
    return !!this.filterActive || !!this.filterType || !!this.filterIsDemo || this.filterDeleted !== '';
  }

  protected get activeFilterCount(): number {
    return (this.filterActive ? 1 : 0) + (this.filterType ? 1 : 0) + (this.filterIsDemo ? 1 : 0) + (this.filterDeleted !== '' ? 1 : 0);
  }

  protected get typeOptions(): Array<{value: string, text: string}> {
    return Object.values(EServiceProviderType).map(function(v) {
      return {value: v, text: v};
    });
  }

  protected rowClass(item: IShortServiceProvider): string {
    if (item.is_demo) return 'demo-row';
    return '';
  }

  protected loadData() {
    this.fetchServiceProvidersList(this.buildFilters());
  }

  protected buildFilters(): any {
    const filters: any = {page: 1, per_page: this.selectedPerPage, deleted: this.filterDeleted};
    if (this.search) filters.search = this.search;
    if (this.filterActive !== '') filters.active = this.filterActive;
    if (this.filterType) filters.type = this.filterType;
    if (this.filterIsDemo !== '') filters.is_demo = this.filterIsDemo;
    filters.sort_by = this.sortBy || 'id';
    filters.sort_order = this.sortDesc ? 'desc' : 'asc';
    return filters;
  }

  protected onSearchInput() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.reload();
    }, 400);
  }

  protected onClearSearch() {
    this.search = '';
    this.reload();
  }

  protected noopSort(items: any[]): any[] {
    return items;
  }

  protected onUpdateOptions(options: any) {
    const newSortBy = Array.isArray(options.sortBy) ? (options.sortBy[0] || 'id') : (options.sortBy || 'id');
    const newSortDesc = Array.isArray(options.sortDesc) ? !!options.sortDesc[0] : !!options.sortDesc;
    if (newSortBy !== this.sortBy || newSortDesc !== this.sortDesc) {
      this.sortBy = newSortBy;
      this.sortDesc = newSortDesc;
      clearTimeout(this.sortTimer);
      this.sortTimer = setTimeout(() => { this.reload(); }, 0);
    }
  }

  protected onFilterChange() {
    this.reload();
  }

  protected onTypeChipClick(value: string) {
    this.filterType = this.filterType === value ? '' : value;
    this.reload();
  }

  protected onDeletedChipClick(value: string) {
    this.filterDeleted = value;
    this.reload();
  }

  protected onToggleDeletedFilter() {
    this.filterDeleted = this.filterDeleted !== '' ? '' : 'all';
    this.reload();
  }

  protected reload() {
    this.fetchServiceProvidersList(this.buildFilters());
  }

  protected onPageChange(page: number) {
    const filters: any = this.buildFilters();
    filters.page = page;
    this.fetchServiceProvidersList(filters);
  }

  protected onPerPageChange(value: number) {
    this.selectedPerPage = value;
    this.reload();
  }

  protected onCreate() {
    this.$router.push(UrlHelper.mayorServiceProviderCreate());
  }

  protected onShowContextMenu(e: MouseEvent, eVT: {item: IShortServiceProvider}) {
    e.preventDefault();
    this.showContextMenu = false;
    this.contextMenuX = e.clientX;
    this.contextMenuY = e.clientY;
    this.contextMenuTarget = eVT.item;
    setTimeout(() => this.showContextMenu = true);
  }

  protected onToggleDemo(item: IShortServiceProvider) {
    this.setDemo({serviceProviderId: item.id, isDemo: !item.is_demo});
  }

  protected showDuplicate(spId: number) {
    this.spId = spId;
    this.showDuplicateDialog = true;
  }

  protected onCloseDuplicate() {
    this.spId = 0;
    this.showDuplicateDialog = false;
  }

  protected get csvHeaders(): string[] {
    return [
      'id', 'name', 'type', 'status', 'active', 'is_demo',
      'email', 'phone_number', 'phone_number_mobile', 'phone_for_queries', 'website',
      'address_line_1', 'address_line_2', 'address_town_city', 'address_county', 'address_postcode', 'address_country_id',
      'invoicing_address',
      'tax_id_number', 'tax_percentage', 'payment_provider',
      'link', 'color',
      'autoconfirm', 'has_item_tracking', 'send_communications', 'use_only_restricted_cells', 'uploads_enabled', 'display_locker',
      'extensions', 'delivery_address',
      'service_price', 'calculation_unit', 'range_min', 'range_max', 'days_available', 'subscription_enabled',
      'precheck_url', 'purchase_url',
      'scope_country_id', 'city_ids', 'zipcodes', 'verticals',
      'created_at', 'updated_at', 'deleted_at',
    ];
  }

  protected csvMapRow(item: any): Array<string | number | null | undefined> {
    return [
      item.id,
      item.name || '',
      item.type || '',
      item.status || '',
      item.active ? 'true' : 'false',
      item.is_demo ? 'true' : 'false',
      item.email || '',
      item.phone_number || '',
      item.phone_number_mobile || '',
      item.phone_for_queries || '',
      item.website || '',
      item.address_line_1 || '',
      item.address_line_2 || '',
      item.address_town_city || '',
      item.address_county || '',
      item.address_postcode || '',
      item.address_country_id || '',
      item.invoicing_address || '',
      item.tax_id_number || '',
      item.tax_percentage != null ? item.tax_percentage : '',
      item.payment_provider || '',
      item.link || '',
      item.color || '',
      item.autoconfirm ? 'true' : 'false',
      item.has_item_tracking ? 'true' : 'false',
      item.send_communications ? 'true' : 'false',
      item.use_only_restricted_cells ? 'true' : 'false',
      item.uploads_enabled ? 'true' : 'false',
      item.display_locker ? 'true' : 'false',
      Array.isArray(item.extensions) ? item.extensions.join(', ') : (item.extensions || ''),
      item.delivery_address || '',
      item.service_price != null ? item.service_price : '',
      item.calculation_unit || '',
      item.range_min != null ? item.range_min : '',
      item.range_max != null ? item.range_max : '',
      Array.isArray(item.days_available) ? item.days_available.join(', ') : (item.days_available || ''),
      item.subscription_enabled ? 'true' : 'false',
      item.precheck_url || '',
      item.purchase_url || '',
      item.scope_country_id != null ? item.scope_country_id : '',
      Array.isArray(item.city_ids) ? item.city_ids.join(', ') : (item.city_ids || ''),
      Array.isArray(item.zipcodes) ? item.zipcodes.join(', ') : (item.zipcodes || ''),
      Array.isArray(item.verticals) ? item.verticals.join(', ') : (item.verticals || ''),
      item.created_at || '',
      item.updated_at || '',
      item.deleted_at || '',
    ];
  }

  protected fetchServiceProvidersCount(): Promise<number> {
    return serviceProviderService.fetchServiceProvidersCount();
  }

  protected fetchServiceProvidersCsv(): Promise<any[]> {
    return serviceProviderService.exportServiceProviders();
  }

}
